import api from './api';
import {
  CertifiedTrainerApplicationStatus,
  CertifiedTrainerApplication,
  CertifiedTrainerDashboard,
  Course,
  Lesson,
  Enrollment,
  ExamAccess,
  TrainerNotification,
  TrainerMember,
} from '../types';

export const certifiedTrainerService = {
  // Application endpoints
  async getApplicationStatus(): Promise<CertifiedTrainerApplicationStatus> {
    const response = await api.get('/trainer/apply/status');
    return response.data;
  },

  async submitApplication(data: {
    bio: string;
    expertise: string[];
    portfolioLinks?: { label: string; url: string }[];
  }): Promise<{ message: string; application: CertifiedTrainerApplication }> {
    const response = await api.post('/trainer/apply', data);
    return response.data;
  },

  async uploadDocument(
    file: File,
    name: string,
    type: 'certificate' | 'degree' | 'id' | 'other'
  ): Promise<{ message: string; document: { name: string; url: string; type: string } }> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('name', name);
    formData.append('type', type);

    const response = await api.post('/trainer/apply/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Certified Trainer dashboard endpoints
  async getDashboard(): Promise<CertifiedTrainerDashboard> {
    const response = await api.get('/trainer/dashboard');
    return response.data;
  },

  async getCourses(): Promise<Course[]> {
    const response = await api.get('/trainer/courses');
    return response.data;
  },

  async createCourse(data: Partial<Course>): Promise<Course> {
    const response = await api.post('/trainer/courses', data);
    return response.data;
  },

  async updateCourse(id: string, data: Partial<Course>): Promise<Course> {
    const response = await api.put(`/trainer/courses/${id}`, data);
    return response.data;
  },

  async submitCourseForReview(id: string): Promise<{ message: string }> {
    const response = await api.post(`/trainer/courses/${id}/submit`);
    return response.data;
  },

  async publishCourse(id: string): Promise<{ message: string }> {
    const response = await api.post(`/trainer/courses/${id}/publish`);
    return response.data;
  },

  async getCourseStudents(courseId: string): Promise<Enrollment[]> {
    const response = await api.get(`/trainer/courses/${courseId}/students`);
    return response.data;
  },

  async updateEnrollment(
    enrollmentId: string,
    action: 'approve' | 'reject'
  ): Promise<{ message: string }> {
    const response = await api.put(`/trainer/enrollments/${enrollmentId}`, { action });
    return response.data;
  },

  // Set/extend a student's course-access expiry (YYYY-MM-DD, or '' to clear).
  async setEnrollmentExpiry(
    enrollmentId: string,
    expiresAt: string | null
  ): Promise<{ message: string; expiresAt: string | null }> {
    const response = await api.put(`/trainer/enrollments/${enrollmentId}/expiry`, { expiresAt: expiresAt ?? '' });
    return response.data;
  },

  // Lesson management
  async getLessons(courseId: string): Promise<Lesson[]> {
    const response = await api.get(`/trainer/courses/${courseId}/lessons`);
    return response.data;
  },

  async createLesson(courseId: string, data: Partial<Lesson>): Promise<Lesson> {
    const response = await api.post(`/trainer/courses/${courseId}/lessons`, data);
    return response.data;
  },

  async updateLesson(courseId: string, lessonId: string, data: Partial<Lesson>): Promise<Lesson> {
    const response = await api.put(`/trainer/courses/${courseId}/lessons/${lessonId}`, data);
    return response.data;
  },

  async deleteLesson(courseId: string, lessonId: string): Promise<{ message: string }> {
    const response = await api.delete(`/trainer/courses/${courseId}/lessons/${lessonId}`);
    return response.data;
  },

  // All members across all courses (deduplicated)
  async getAllMembers(): Promise<TrainerMember[]> {
    const response = await api.get('/trainer/members');
    return response.data;
  },

  // Pending enrollments (across all courses)
  async getPendingEnrollments(): Promise<Enrollment[]> {
    const response = await api.get('/trainer/pending-enrollments');
    return response.data;
  },

  // Exam management
  async getExams(): Promise<any[]> {
    const response = await api.get('/trainer/exams');
    return response.data;
  },

  async getExamDashboard(): Promise<any[]> {
    const response = await api.get('/trainer/exams/dashboard');
    return response.data;
  },

  async getExam(examId: string): Promise<any> {
    const response = await api.get(`/trainer/exams/${examId}`);
    return response.data;
  },

  async createExam(data: any): Promise<any> {
    const response = await api.post('/trainer/exams', data);
    return response.data;
  },

  async updateExam(examId: string, data: any): Promise<any> {
    const response = await api.put(`/trainer/exams/${examId}`, data);
    return response.data;
  },

  async deleteExam(examId: string): Promise<{ message: string }> {
    const response = await api.delete(`/trainer/exams/${examId}`);
    return response.data;
  },

  async createQuestion(examId: string, data: any): Promise<any> {
    const response = await api.post(`/trainer/exams/${examId}/questions`, data);
    return response.data;
  },

  async updateQuestion(examId: string, questionId: string, data: any): Promise<any> {
    const response = await api.put(`/trainer/exams/${examId}/questions/${questionId}`, data);
    return response.data;
  },

  async deleteQuestion(examId: string, questionId: string): Promise<{ message: string }> {
    const response = await api.delete(`/trainer/exams/${examId}/questions/${questionId}`);
    return response.data;
  },

  async updateExamPrerequisites(examId: string, data: { courseIds?: string[]; examIds?: string[] }): Promise<{ message: string }> {
    const response = await api.put(`/trainer/exams/${examId}/prerequisites`, data);
    return response.data;
  },

  async getExamAttempts(examId: string): Promise<any[]> {
    const response = await api.get(`/trainer/exams/${examId}/attempts`);
    return response.data;
  },

  async resetExamAttempts(examId: string, userId: string): Promise<{ message: string; deletedCount: number }> {
    const response = await api.post(`/trainer/exams/${examId}/reset-attempts/${userId}`);
    return response.data;
  },

  // Per-student exam access (list + extend), own exams only.
  async getExamAccess(examId: string): Promise<ExamAccess[]> {
    const response = await api.get(`/trainer/exams/${examId}/access`);
    return response.data;
  },

  async setExamAccessExpiry(
    accessId: string,
    expiresAt: string | null
  ): Promise<{ message: string; expiresAt: string | null }> {
    const response = await api.put(`/trainer/exam-access/${accessId}/expiry`, { expiresAt: expiresAt ?? '' });
    return response.data;
  },

  async getAllCertificates(): Promise<any[]> {
    const response = await api.get('/trainer/certificates');
    return response.data;
  },

  async getExamCertificates(examId: string): Promise<any[]> {
    const response = await api.get(`/trainer/exams/${examId}/certificates`);
    return response.data;
  },

  // Certificate logo (per-trainer branding)
  async getCertificateLogo(): Promise<{ url: string | null }> {
    const response = await api.get('/trainer/certificate-logo');
    return response.data;
  },

  async uploadCertificateLogo(file: File): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('logo', file);
    const response = await api.post('/trainer/certificate-logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteCertificateLogo(): Promise<{ message: string }> {
    const response = await api.delete('/trainer/certificate-logo');
    return response.data;
  },

  async previewCertificateBlob(args: { courseId?: string; examId?: string; templateId?: string }): Promise<Blob> {
    const response = await api.post('/trainer/certificates/preview', args, { responseType: 'blob' });
    return response.data as Blob;
  },

  async downloadCertificateBlob(certId: string): Promise<Blob> {
    const response = await api.get(`/trainer/certificates/${certId}/download`, { responseType: 'blob' });
    return response.data as Blob;
  },

  async listAccessibleCertificateTemplates(): Promise<Array<{
    _id: string;
    name: string;
    description: string;
    visibility: 'public' | 'restricted';
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
  }>> {
    const response = await api.get('/trainer/certificate-templates');
    return response.data;
  },

  async setExamCertificateTemplate(examId: string, templateId: string | null): Promise<void> {
    await api.put(`/trainer/exams/${examId}/certificate-template`, { templateId: templateId || '' });
  },

  // Promo code management
  async getPromoCodes(): Promise<any[]> {
    const response = await api.get('/trainer/promo-codes');
    return response.data;
  },

  async createPromoCode(data: any): Promise<any> {
    const response = await api.post('/trainer/promo-codes', data);
    return response.data;
  },

  async updatePromoCode(id: string, data: any): Promise<any> {
    const response = await api.put(`/trainer/promo-codes/${id}`, data);
    return response.data;
  },

  async deletePromoCode(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/trainer/promo-codes/${id}`);
    return response.data;
  },

  async getPromoCodeUsage(id: string): Promise<any[]> {
    const response = await api.get(`/trainer/promo-codes/${id}/usage`);
    return response.data;
  },

  // Payment capabilities
  async getPaymentCapabilities(): Promise<{
    customPaymentEnabled: boolean;
    hasStripeConfig: boolean;
    canConfigureStripe: boolean;
    customPaymentMethodAllowed: boolean;
  }> {
    const response = await api.get('/trainer/payment-capabilities');
    return response.data;
  },

  // Notifications
  async getNotifications(): Promise<TrainerNotification[]> {
    const response = await api.get('/trainer/notifications');
    return response.data;
  },

  async getUnreadNotificationCount(): Promise<number> {
    const response = await api.get('/trainer/notifications/unread-count');
    return response.data.count;
  },

  async markNotificationAsRead(id: string): Promise<void> {
    await api.put(`/trainer/notifications/${id}/read`);
  },

  async markAllNotificationsAsRead(): Promise<void> {
    await api.put('/trainer/notifications/read-all');
  },

  async markNotificationsAsRead(ids: string[]): Promise<void> {
    await api.put('/trainer/notifications/read', { ids });
  },

  async deleteNotifications(ids: string[]): Promise<void> {
    await api.post('/trainer/notifications/delete', { ids });
  },

  async getVideoStatus(): Promise<{
    courseId: string;
    courseTitle: string;
    lessonId: string;
    lessonTitle: string;
    mediaId: string;
    videoStatus: string;
    progress: number;
    error: string | null;
    fileSize: number | null;
    originalFilename: string | null;
    url: string | null;
  }[]> {
    const response = await api.get('/trainer/video-status');
    return response.data;
  },
};

export default certifiedTrainerService;
